import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { PartnerGroupRules } from './group-rules.types';
import { usePartnerGroupRules } from './partner-group-rules';

/**
 * Entities whose platform-catalog (partnerId === PLATFORM_PARTNER_ID) rows are
 * gated by a per-tenant rule. Mirrors the backend `PLATFORM_CATALOG_POLICY`
 * registry; keep these two in sync when adding a new gated entity.
 */
export type PlatformCatalogEntity =
  | 'Behavior'
  | 'CustomView'
  | 'Macro'
  | 'CustomAttributeDefinition'
  | 'ConversationAssigneeGroup'
  | 'UserGroup';

const PLATFORM_CATALOG_RULE_BY_ENTITY: Record<PlatformCatalogEntity, PartnerGroupRules> = {
  Behavior: PartnerGroupRules.AccessToPlatformBehaviors,
  CustomView: PartnerGroupRules.AccessToPlatformCustomViews,
  Macro: PartnerGroupRules.AccessToPlatformMacros,
  CustomAttributeDefinition: PartnerGroupRules.AccessToPlatformCustomAttributes,
  ConversationAssigneeGroup: PartnerGroupRules.AccessToPlatformAssigneeGroups,
  UserGroup: PartnerGroupRules.AccessToPlatformUserGroups,
};

interface PlatformCatalogAccessStateInput {
  scopedPartnerId?: number | null;
  fallbackPartnerId?: number | null;
  partnerGroupRules: Partial<Record<PartnerGroupRules, boolean>>;
  requiredRule: PartnerGroupRules;
}

interface PlatformCatalogAccessState {
  includePlatformLevelData: boolean;
  ruleLookupPartnerId: number | null;
}

export function getPlatformCatalogAccessState(
  input: PlatformCatalogAccessStateInput,
): PlatformCatalogAccessState {
  const ruleLookupPartnerId = getRuleLookupPartnerId(
    input.scopedPartnerId,
    input.fallbackPartnerId,
  );

  if (isNullish(ruleLookupPartnerId) || ruleLookupPartnerId === PLATFORM_PARTNER_ID) {
    return {
      includePlatformLevelData: true,
      ruleLookupPartnerId,
    };
  }

  return {
    includePlatformLevelData: input.partnerGroupRules[input.requiredRule] === true,
    ruleLookupPartnerId,
  };
}

/**
 * One-stop hook for entity-list hooks (e.g. `useBehaviors`, `useCustomViews`,
 * `useSettingsMacros`, `useChatMacros`, `useCustomAttributeDefinitions`) that
 * need to know whether the supplied partner is allowed to see platform-level
 * rows of `entity`.
 *
 * Encapsulates the partnerId derivation, current-user fallback, and rule
 * lookup so handlers consume `{ includePlatformLevelData, ruleLookupPartnerId }`
 * instead of repeating the same boilerplate.
 */
export function usePlatformCatalogAccess(
  entity: PlatformCatalogEntity,
  partnerId?: number | null,
): PlatformCatalogAccessState {
  const { user } = useCurrentUserProfile();
  const requiredRule = PLATFORM_CATALOG_RULE_BY_ENTITY[entity];
  const fallbackPartnerId = user?.partnerId ?? null;
  const scopedPartnerId = partnerId ?? null;
  const ruleLookupPartnerId = getRuleLookupPartnerId(scopedPartnerId, fallbackPartnerId);

  const { partnerGroupRules } = usePartnerGroupRules(ruleLookupPartnerId);

  return getPlatformCatalogAccessState({
    scopedPartnerId,
    fallbackPartnerId,
    partnerGroupRules,
    requiredRule,
  });
}

/**
 * Returns a predicate that hides platform-owned items when the caller does
 * not have access. Use as `.filter(excludePlatformWhenDenied(includePlatform))`
 * to collapse the repeated `!(!includePlatform && partnerId === PLATFORM)`
 * post-filter that lived in three of the four hooks.
 */
export function excludePlatformWhenDenied(
  includePlatform: boolean,
): (item: { partnerId?: number | null }) => boolean {
  if (includePlatform) {
    return () => true;
  }
  return (item) => item.partnerId !== PLATFORM_PARTNER_ID;
}

function getRuleLookupPartnerId(
  scopedPartnerId?: number | null,
  fallbackPartnerId?: number | null,
): number | null {
  if (!isNullish(scopedPartnerId) && scopedPartnerId !== PLATFORM_PARTNER_ID) {
    return scopedPartnerId;
  }

  return fallbackPartnerId ?? null;
}
